import React, { useEffect, useState } from 'react'
import {Video, Headphones, FileText, Calendar, X} from 'lucide-react'
import { motion } from 'framer-motion'
import { lumi } from '../lib/lumi'

const Sermons: React.FC = () => {
  const [sermons, setSermons] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedSermon, setSelectedSermon] = useState<any>(null)

  useEffect(() => {
    const fetchSermons = async () => {
      try {
        const response = await lumi.entities.sermons.list({
          sort: { date: -1 },
          limit: 100
        })
        setSermons(response?.list || [])
      } catch (error) {
        console.error('Erro ao carregar mensagens:', error)
        setSermons([])
      } finally {
        setLoading(false)
      }
    }
    fetchSermons()
  }, [])

  const getTypeIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'VIDEO':
        return <Video size={20} />
      case 'AUDIO':
        return <Headphones size={20} />
      case 'TEXT':
        return <FileText size={20} />
      default:
        return <Video size={20} />
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'VIDEO':
        return 'Vídeo'
      case 'AUDIO':
        return 'Áudio'
      case 'TEXT':
        return 'Texto'
      default:
        return 'Vídeo'
    }
  }

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return null
    
    try {
      // Regex universal para todos os formatos do YouTube
      const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
      const match = url.match(regExp)
      const videoId = (match && match[7]?.length === 11) ? match[7] : null
      
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null
    } catch (error) {
      console.error('Erro ao processar URL do YouTube:', error)
      return null
    }
  }

  const handleWatchSermon = (sermon: any) => {
    setSelectedSermon(sermon)
  }

  const closeModal = () => {
    setSelectedSermon(null)
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#6b8f71] py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Palavra de Deus
            </h1>
            <p className="text-xl text-white/90">
              Mensagens que edificam e fortalecem sua fé
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lista de Mensagens */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
            </div>
          ) : sermons.length === 0 ? (
            <div className="text-center py-12">
              <Video className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500">Nenhuma mensagem disponível no momento.</p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {sermons.map((sermon, index) => (
              <motion.div
                key={sermon._id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden group cursor-pointer"
              >
                <div className="relative h-48 bg-gray-200 overflow-hidden">
                  {sermon.thumbnailUrl ? (
                    <img 
                      src={sermon.thumbnailUrl} 
                      alt={sermon.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1e3a8a] to-[#6b8f71]">
                      {getTypeIcon(sermon.type)}
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-[#1e3a8a] text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                    {getTypeIcon(sermon.type)}
                    {getTypeLabel(sermon.type)}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <Calendar size={16} />
                    {sermon.date ? new Date(sermon.date).toLocaleDateString('pt-BR') : 'Data não informada'}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1e3a8a] mb-2">
                    {sermon.title}
                  </h3>
                  <p className="text-[#6b8f71] font-semibold mb-3">
                    {sermon.speaker}
                  </p>
                  {sermon.description && (
                    <p className="text-gray-600 mb-4">
                      {sermon.description}
                    </p>
                  )}
                  <button 
                    onClick={() => handleWatchSermon(sermon)}
                    className="w-full bg-[#6b8f71] text-white py-3 rounded-lg font-semibold hover:bg-[#5a7760] transition-all flex items-center justify-center gap-2"
                  >
                    {getTypeIcon(sermon.type)}
                    Assistir/Ouvir Mensagem
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Modal de Vídeo */}
      {selectedSermon && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-4xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-bold text-[#1e3a8a]">{selectedSermon.title}</h3>
              <button 
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="aspect-video bg-black">
              {getYouTubeEmbedUrl(selectedSermon.videoUrl || selectedSermon.mediaUrl) ? (
                <iframe
                  width="100%"
                  height="100%"
                  src={getYouTubeEmbedUrl(selectedSermon.videoUrl || selectedSermon.mediaUrl) || ''}
                  title={selectedSermon.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <p>Vídeo não disponível</p>
                </div>
              )}
            </div>
            {selectedSermon.description && (
              <div className="p-4">
                <p className="text-gray-600">{selectedSermon.description}</p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default Sermons
